import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers } from '@fortawesome/free-solid-svg-icons';
import Circles from '../../../assets/images/circle.png';
import axios from 'axios';

const FmttiDashboard = ({ 
  loading, 
  handleMoreInfoClick 
}) => {
  // Local state for FMTTI Admin specific data
  const [userCount, setUserCount] = useState(0);
  const [demonstrationCount, setDemonstrationCount] = useState(0);
  const [localLoading, setLocalLoading] = useState(false);
  const [userDetails, setUserDetails] = useState([]);
  const [demonstrationDetails, setDemonstrationDetails] = useState([]);
  const [activeTable, setActiveTable] = useState('none'); // 'none', 'user', 'demonstration'

  // Helper function for CSV export
  const exportToCSV = (data, filename, headers) => {
    if (!data || data.length === 0) {
      alert("No data available to export.");
      return;
    }

    const csvRows = [headers.join(",")];

    data.forEach((item, index) => {
      const row = [
        index + 1,
        `"${item.name || item.stateName || item.districtName || 'N/A'}"`,
        item.count || item.total || item.totalUsers || 0
      ];
      csvRows.push(row.join(","));
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Fetch FMTTI Dashboard Report Count
  const fetchFMTTIDashboardData = async () => {
    try {
      setLocalLoading(true);
      
      const { data } = await axios.post(
        `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/GetFMTTIDashboarReportCount`,
        {
          "flag": "FMTC",
          "fmttiCode": sessionStorage.getItem("fmttiCode")
        },
        {
          headers: {
            'accept': '*/*',
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('FMTTI Dashboard API Response:', data);

      if (data.success) {
        // Extract user count and demonstration count from response
        // Adjust these based on actual API response structure
        if (data.dataContent && Array.isArray(data.dataContent)) {
          // If dataContent is an array, process it
          const userCountValue = data.dataContent.find(item => item.type === 'user')?.count || 
                                 data.dataContent[0]?.userCount || 
                                 data.dataContent[0]?.totalUsers || 
                                 data.recordCount || 
                                 0;
          
          const demoCountValue = data.dataContent.find(item => item.type === 'demonstration')?.count || 
                                data.dataContent[0]?.demonstrationCount || 
                                data.dataContent[0]?.total || 
                                data.total || 
                                0;

          setUserCount(parseInt(userCountValue.toString()) || 0);
          setDemonstrationCount(parseInt(demoCountValue.toString()) || 0);
        } else if (data.userCount !== undefined || data.demonstrationCount !== undefined) {
          // If counts are at root level
          setUserCount(parseInt(data.userCount?.toString() || data.recordCount?.toString() || '0') || 0);
          setDemonstrationCount(parseInt(data.demonstrationCount?.toString() || data.total?.toString() || '0') || 0);
        } else {
          // Fallback to recordCount or total
          setUserCount(parseInt(data.recordCount?.toString() || '0') || 0);
          setDemonstrationCount(parseInt(data.total?.toString() || '0') || 0);
        }
      } else {
        setUserCount(0);
        setDemonstrationCount(0);
        console.log('FMTTI Dashboard API returned success: false');
      }
    } catch (error) {
      console.error('Error fetching FMTTI Dashboard data:', error);
      setUserCount(0);
      setDemonstrationCount(0);
    } finally {
      setLocalLoading(false);
    }
  };

  // Fetch user details for More Info
  const fetchUserDetails = async () => {
    try {
      setLocalLoading(true);
      
      const { data } = await axios.post(
        `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/GetFMTTIDashboarReportCount`,
        {
          "flag": "Surveyor",
          "fmttiCode": sessionStorage.getItem("fmttiCode")
        },
        {
          headers: {
            'accept': '*/*',
            'Content-Type': 'application/json'
          }
        }
      );

      if (data.success && data.dataContent) {
        // Filter or transform data for user details
        // Adjust based on actual API response structure
        setUserDetails(Array.isArray(data.dataContent) ? data.dataContent : []);
      } else {
        setUserDetails([]);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      setUserDetails([]);
    } finally {
      setLocalLoading(false);
    }
  };
  //alert(sessionStorage.getItem("fmttiCode"));
  // Fetch demonstration details for More Info
  const fetchDemonstrationDetails = async () => {
    try {
      setLocalLoading(true);
      
      const { data } = await axios.post(
        `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/GetFMTTIDashboarReportCount`,
        {
          "flag": "FMTC",
          "fmttiCode": sessionStorage.getItem("fmttiCode")
        },
        {
          headers: {
            'accept': '*/*',
            'Content-Type': 'application/json'
          }
        }
      );

      if (data.success && data.dataContent) {
        // Filter or transform data for demonstration details
        // Adjust based on actual API response structure
        setDemonstrationDetails(Array.isArray(data.dataContent) ? data.dataContent : []);
      } else {
        setDemonstrationDetails([]);
      }
    } catch (error) {
      console.error('Error fetching demonstration details:', error);
      setDemonstrationDetails([]);
    } finally {
      setLocalLoading(false);
    }
  };

  // Handle More Info clicks locally
  const handleLocalMoreInfo = async (type) => {
    // If clicking the same table, toggle it off
    if (activeTable === type) {
      setActiveTable('none');
      return;
    }

    // Set the new active table
    setActiveTable(type);

    if (type === 'user') {
      await fetchUserDetails();
    } else if (type === 'demonstration') {
      await fetchDemonstrationDetails();
    } else if (typeof handleMoreInfoClick === "function") {
      handleMoreInfoClick(type);
    }
  };

  // Fetch data when component mounts
  useEffect(() => {
    fetchFMTTIDashboardData();
  }, []);

  return (
    <>
      {/* Active User Card */}
      <div className="col-xxl-6 col-xl-6">
        <div className="card info-card sales-card bg-gradient-danger card-img-holder">
          <div className="card-body">
            <img src={Circles} className="card-img-absolute" alt="circle-image" />
            <h5 className="card-title">Active User</h5>
            <div className="d-flex align-items-center">
              <div className="card-icon rounded-circle d-flex align-items-center justify-content-center">
                <i><FontAwesomeIcon icon={faUsers} /> </i>
              </div>
              <div className="ps-3">
                <h6 id="recordCount">{loading || localLoading ? 'Loading...' : userCount}</h6>
              </div>
            </div>
            <div className="mt-3">
              <button
                className='btn btn-link p-0 text-white'
                onClick={() => handleLocalMoreInfo('user')}
                title="Click for more information"
              >
                <i className="bi bi-arrow-right-circle-fill fs-6">More Info</i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Demonstration Count Card */}
      <div className="col-xxl-6 col-xl-6">
        <div className="card info-card sales-card bg-gradient-primary card-img-holder">
          <div className="card-body">
            <img src={Circles} className="card-img-absolute" alt="circle-image" />
            <h5 className="card-title">Demonstration Count</h5>
            <div className="d-flex align-items-center">
              <div className="card-icon rounded-circle d-flex align-items-center justify-content-center">
                <i><FontAwesomeIcon icon={faUsers} /> </i>
              </div>
              <div className="ps-3">
                <h6 id="chcHistoryCount">{loading || localLoading ? 'Loading...' : demonstrationCount}</h6>
              </div>
            </div>
            <div className="mt-3">
              <button
                className='btn btn-link p-0 text-white'
                onClick={() => handleLocalMoreInfo('demonstration')}
                title="Click for more information"
              >
                <i className="bi bi-arrow-right-circle-fill fs-6">More Info</i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Active Users Details Table */}
      {activeTable === 'user' && (
        <div className="col-12">
          <h5>Active Users Details</h5>
          <div className="d-flex justify-content-end mb-2">
            <button 
              className="btn btn-success" 
              onClick={() => exportToCSV(userDetails, "FMTTI_Active_Users_Report", ["S.No", "Name", "Count"])}
            >
              Export CSV
            </button>
          </div>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr className="table-light">
                  <th>Sr. No.</th>
                  <th>Name</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {userDetails && userDetails.length > 0 ? (
                  userDetails.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{item.name || item.stateName || item.districtName || item.userName || 'N/A'}</td>
                      <td>{item.count || item.total || item.totalUsers || 0}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="text-center">
                      {localLoading ? 'Loading...' : 'No user data available'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Demonstration Details Table */}
      {activeTable === 'demonstration' && (
        <div className="col-12">
          <h5>Demonstration Details</h5>
          <div className="d-flex justify-content-end mb-2">
            <button 
              className="btn btn-success" 
              onClick={() => exportToCSV(demonstrationDetails, "FMTTI_Demonstration_Report", ["S.No", "Name", "Count"])}
            >
              Export CSV
            </button>
          </div>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr className="table-light">
                  <th>Sr. No.</th>
                  <th>Name</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {demonstrationDetails && demonstrationDetails.length > 0 ? (
                  demonstrationDetails.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{item.name || item.stateName || item.districtName || item.demonstrationName || 'N/A'}</td>
                      <td>{item.count || item.total || item.demonstrationCount || 0}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="text-center">
                      {localLoading ? 'Loading...' : 'No demonstration data available'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default FmttiDashboard;

